/**
 * Authentication Service
 * API calls for authentication and registration
 */

import api, { clearTokens } from './api';

// Types
export type RegistrationType =
  | 'TRANSPORTEUR_FREELANCE'
  | 'TRANSPORTEUR_ENTREPRISE'
  | 'CONDUCTEUR_FREELANCE';

export type OTPType = 'SIGNUP' | 'EMAIL_VALIDATION' | 'PHONE_VALIDATION' | 'PASSWORD_RESET';

export interface SendOTPRequest {
  email?: string;
  phone?: string;
  dial_code?: string;
  type: OTPType;
}

export interface VerifyOTPRequest {
  email?: string;
  phone?: string;
  dial_code?: string;
  code: string;
  type: OTPType;
}

export interface OTPResponse {
  success: boolean;
  message: string;
  expires_in_seconds?: number;
}

export interface UserRequest {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  dial_code: string;
  password: string;
  gender?: 'MALE' | 'FEMALE';
  address?: string;
  niu?: string;
  country_id?: number;
  birth_place_id?: number;
}

export interface StructureRequest {
  name: string;
  email: string;
  phone?: string;
  dial_code: string;
  address?: string;
  niu?: string;
  description?: string;
  country_id?: number;
  division_id?: number;
}

export type DocumentType = 'ID_CARD' | 'PASSPORT' | 'DRIVING_LICENSE' | 'OTHER';
export type LicenceCategory = 'A' | 'B' | 'C' | 'D';

export interface UserLicenceRequest {
  document_type: DocumentType;
  document_number: string;
  issue_date?: string;
  expiry_date?: string;
  issue_place?: string;
  licence_category?: LicenceCategory;
}

export interface SignUpRequest {
  registration_type: RegistrationType;
  user: UserRequest;
  structure?: StructureRequest;
  /** Optional identity document (ID card / passport). */
  identity_document?: UserLicenceRequest;
  /** Driving licence — required for CONDUCTEUR_FREELANCE. */
  driving_license?: UserLicenceRequest;
  user_email_otp_verified: boolean;
  user_phone_otp_verified: boolean;
  structure_email_otp_verified: boolean;
  structure_phone_otp_verified: boolean;
}

export interface UserResponse {
  id: number;
  username?: string;
  email?: string;
  phone?: string;
  dial_code?: string;
  first_name?: string;
  last_name?: string;
  status?: string;
  /**
   * The user's own type (DRIVER, AGENT, AGENCY_HEAD, ...). Distinct from
   * `structure_type`: a driver is a DRIVER inside a CARRIER structure.
   */
  user_type?: string;
  freelance: boolean;
  individual: boolean;
  email_validated: boolean;
  phone_validated: boolean;
  avatar?: string;
  profiles: string[];
  structure_id?: number;
  structure_name?: string;
  structure_type?: string;
  structure_individual: boolean;
  is_main_ogf: boolean;
  is_structure_admin: boolean;
  agency_id?: number;
  agency_name?: string;
}

export interface StructureResponse {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  dial_code?: string;
  address?: string;
  niu?: string;
  status?: string;
  type?: string;
  individual: boolean;
  email_validated: boolean;
  phone_validated: boolean;
}

export interface SignUpResponse {
  success: boolean;
  message: string;
  user?: UserResponse;
  structure?: StructureResponse;
  /** True when the account can log in right away (no validation pending) */
  account_active?: boolean;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface ForgotPasswordRequest {
  identifier: string;
  dial_code?: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
  sent_via?: 'email' | 'sms';
  masked_destination?: string;
}

export interface ResetPasswordRequest {
  identifier: string;
  dial_code?: string;
  code: string;
  new_password: string;
}

export interface CreatePasswordRequest {
  token: string;
  password: string;
}

export interface CreatePasswordResponse {
  success: boolean;
  message: string;
  user?: UserResponse;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  /**
   * Present ONLY when the server issued HttpOnly session cookies.
   *
   * This is the capability signal the client keys off: with it, stop sending
   * Authorization and echo this value in X-CSRF-Token instead; without it, the
   * server is still on the legacy bearer scheme.
   */
  csrf_token?: string | null;
  token_type: string;
  user_id: number;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  must_change_password: boolean;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export interface RequestEmailChangeRequest {
  user_id: number;
  new_email: string;
}

export interface ConfirmEmailChangeRequest {
  user_id: number;
  new_email: string;
  code: string;
}

// API Functions
export const authService = {
  /**
   * Send OTP code to email or phone
   */
  sendOTP: async (data: SendOTPRequest): Promise<OTPResponse> => {
    const response = await api.post<OTPResponse>('/auth/send-otp', data);
    return response.data;
  },

  /**
   * Verify OTP code
   */
  verifyOTP: async (data: VerifyOTPRequest): Promise<OTPResponse> => {
    const response = await api.post<OTPResponse>('/auth/verify-otp', data);
    return response.data;
  },

  /**
   * Register a new user
   */
  signUp: async (data: SignUpRequest): Promise<SignUpResponse> => {
    const response = await api.post<SignUpResponse>('/auth/signup', data);
    return response.data;
  },

  /**
   * Login with credentials
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', data);
    // Deliberately does NOT persist anything here. The server still returns the
    // JWTs in the body for pre-migration clients, and storing them at this point
    // would write them to localStorage on every login — the exact thing the
    // migration removes — even when the server issued cookies.
    // AuthContext.login() calls startSession(), which reads the csrf_token
    // signal and decides what, if anything, gets stored.
    return response.data;
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearTokens();
    }
  },

  /**
   * Get current user info
   */
  getCurrentUser: async (): Promise<UserResponse> => {
    const response = await api.get<UserResponse>('/auth/me');
    return response.data;
  },

  /**
   * Request password reset (sends OTP to email or phone)
   */
  forgotPassword: async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    const response = await api.post<ForgotPasswordResponse>('/auth/forgot-password', data);
    return response.data;
  },

  /**
   * Reset password using OTP code
   */
  resetPassword: async (data: ResetPasswordRequest): Promise<OTPResponse> => {
    const response = await api.post<OTPResponse>('/auth/reset-password', data);
    return response.data;
  },

  /**
   * Create password using token from email link (for new admin users)
   */
  createPassword: async (data: CreatePasswordRequest): Promise<CreatePasswordResponse> => {
    const response = await api.post<CreatePasswordResponse>('/auth/create-password', data);
    return response.data;
  },

  /**
   * Validate a password creation token (page load check).
   * Returns success=false with a clear message when the link is invalid
   * or has already been used.
   */
  validatePasswordToken: async (token: string): Promise<CreatePasswordResponse> => {
    const response = await api.get<CreatePasswordResponse>(
      '/auth/validate-password-token',
      { params: { token } }
    );
    return response.data;
  },

  /**
   * Change password for logged-in user
   * Used when user must change password on first login (password sent via SMS)
   */
  changePassword: async (data: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
    const response = await api.post<ChangePasswordResponse>('/auth/change-password', data);
    return response.data;
  },

  /**
   * Request an email change: sends an OTP to the new email address.
   * Allowed for the user themselves or for any ADMIN.
   */
  requestEmailChange: async (data: RequestEmailChangeRequest): Promise<OTPResponse> => {
    const response = await api.post<OTPResponse>('/auth/request-email-change', data);
    return response.data;
  },

  /**
   * Confirm an email change with the OTP code sent to the new email.
   */
  confirmEmailChange: async (data: ConfirmEmailChangeRequest): Promise<OTPResponse> => {
    const response = await api.post<OTPResponse>('/auth/confirm-email-change', data);
    return response.data;
  },
};

export default authService;
